/**
 * The per-user undo and redo stacks (PLAN §6.2 "Undo: per-user inverse
 * patches (old values captured when the patch is made). An undo is just a
 * new patch"), with the CLI's semantics (`acp undo`, `apps/cli/src/commands/register/patch.ts`):
 *
 *   - an entry stores what the patch was made against (`rowBefore`, as this
 *     user saw it) and the patch itself; the inverse is computed at undo time
 *     by the domain's `inversePatch`, under a fresh HLC, so it is an ordinary
 *     newer write;
 *   - a field someone else wrote after the patch is skipped
 *     (`undoableFields`) — undoing it would silently overwrite their edit;
 *     an undo's own write does not count as a newer edit for the entry
 *     below it, because it RESTORES the state the undone entry was made on:
 *     `restored` maps each undo/redo write (row, field, HLC) to the HLC it
 *     restored, and `undoableFields` compares through that chain. So A→B→C,
 *     undo, undo reaches A — but if someone else's X landed between B and C,
 *     undoing C restores X, and the next undo sees X and leaves it (second
 *     review: exempting every write of ours wiped X). A non-undoable write of
 *     ours is not in the map, so it counts as a newer edit;
 *     text is always undone through Yjs, which removes only our characters;
 *   - an undo pushes its own inverse onto the redo stack the same way, so a
 *     redo is the undo of the undo; any new edit clears the redo stack.
 *
 * **Typing coalesces.** A script edit is one patch per keystroke batch; a
 * text-only entry on the same row and field as the top entry, within
 * `COALESCE_MS` of it, merges into it (the Yjs updates merged), so Ctrl+Z
 * removes a burst of typing, not one character — but only while nobody
 * else's update landed in between: the merged update is inverted against the
 * top entry's `rowBefore`, and an insert anchored on someone else's
 * characters that state lacks would stay pending, not undone (found in
 * review). So the next entry's base must be exactly the top entry's result.
 *
 * Pure: the caller supplies the current rows and fresh patch metadata.
 */
import { decodeBase64, encodeBase64 } from '@acp/domain/base64.js';
import type { Row, RowPatch } from '@acp/domain/row.js';
import { type InverseMeta, inversePatch, undoableFields } from '@acp/domain/undo.js';
import * as Y from 'yjs';

/** A burst of typing within this long of the previous edit is one undo step. */
export const COALESCE_MS = 1_000;

export type UndoItem = { readonly rowBefore: Row | null; readonly patch: RowPatch };
export type UndoEntry = { readonly items: readonly UndoItem[]; readonly atMs: number };
export type UndoStacks = {
  readonly undo: readonly UndoEntry[];
  readonly redo: readonly UndoEntry[];
  /** `restoredKey(row, field, hlc of an undo/redo write)` → the HLC that write restored (null: never written). */
  readonly restored: ReadonlyMap<string, string | null>;
};

export const EMPTY_STACKS: UndoStacks = { undo: [], redo: [], restored: new Map() };

function restoredKey(rowId: string, path: string, hlc: string): string {
  return `${rowId}\u0000${path}\u0000${hlc}`;
}

/** The undo/redo write `hlc` restored, per field it wrote, the HLC `rowBefore` had there. */
function recordRestored(restored: Map<string, string | null>, rowBefore: Row | null, undone: RowPatch, hlc: string): void {
  for (const path of Object.keys(undone.set)) {
    const before = rowBefore !== null && Object.hasOwn(rowBefore.h, path) ? (rowBefore.h[path] ?? null) : null;
    restored.set(restoredKey(undone.rowId, path, hlc), before);
  }
}

/** The write a field's current HLC stands for, following undo/redo writes back to what they restored. */
function supersededBy(restored: ReadonlyMap<string, string | null>, rowId: string) {
  return (path: string, hlc: string): string | null => {
    let current: string | null = hlc;
    const seen = new Set<string>();
    while (current !== null && !seen.has(current)) {
      seen.add(current);
      const key = restoredKey(rowId, path, current);
      if (!restored.has(key)) return current;
      current = restored.get(key) ?? null;
    }
    return current;
  };
}

/** The most entries kept per stack; the oldest fall off. */
export const MAX_UNDO = 200;

function textOnlyKey(entry: UndoEntry): string | null {
  const [only] = entry.items;
  if (only === undefined || entry.items.length !== 1) return null;
  const { patch } = only;
  const text = Object.keys(patch.text ?? {});
  if (Object.keys(patch.set).length !== 0 || text.length !== 1 || patch.create !== undefined) return null;
  return `${patch.rowId}\u0000${text[0] ?? ''}`;
}

function mergeText(a: string, b: string): string | null {
  const x = decodeBase64(a);
  const y = decodeBase64(b);
  if (x === null || y === null) return null;
  return encodeBase64(Y.mergeUpdates([x, y]));
}

function stateVector(updates: readonly (string | null)[]): Uint8Array | null {
  const doc = new Y.Doc();
  try {
    for (const u of updates) {
      if (u === null) continue;
      const bytes = decodeBase64(u);
      if (bytes === null) return null;
      Y.applyUpdate(doc, bytes);
    }
    return Y.encodeStateVector(doc);
  } catch {
    return null;
  }
}

function sameBytes(a: Uint8Array | null, b: Uint8Array | null): boolean {
  return a !== null && b !== null && a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Whether `second` was made on exactly the text `first` left (no one else's update in between). */
function continues(first: UndoItem, second: UndoItem, field: string): boolean {
  const before = first.rowBefore?.text[field] ?? null;
  const after = stateVector([before, first.patch.text?.[field] ?? null]);
  return sameBytes(after, stateVector([second.rowBefore?.text[field] ?? null]));
}

function coalesce(top: UndoEntry, next: UndoEntry): UndoEntry | null {
  const key = textOnlyKey(top);
  if (key === null || key !== textOnlyKey(next) || next.atMs - top.atMs > COALESCE_MS) return null;
  const [first] = top.items;
  const [second] = next.items;
  if (first === undefined || second === undefined) return null;
  const field = Object.keys(first.patch.text ?? {})[0] ?? '';
  if (!continues(first, second, field)) return null;
  const merged = mergeText(first.patch.text?.[field] ?? '', second.patch.text?.[field] ?? '');
  if (merged === null) return null;
  return { items: [{ rowBefore: first.rowBefore, patch: { ...second.patch, text: { [field]: merged } } }], atMs: next.atMs };
}

/** Record a new edit: onto the undo stack (merged into a typing burst where it continues one); clears redo. */
export function pushEdit(stacks: UndoStacks, entry: UndoEntry): UndoStacks {
  const top = stacks.undo.at(-1);
  const merged = top === undefined ? null : coalesce(top, entry);
  const undo = merged === null ? [...stacks.undo, entry] : [...stacks.undo.slice(0, -1), merged];
  return { undo: undo.slice(-MAX_UNDO), redo: [], restored: stacks.restored };
}

export type UndoPlan =
  | {
      readonly ok: true;
      /** The inverse patches to apply and send. */
      readonly patches: readonly RowPatch[];
      /** Fields skipped because someone else wrote them since. */
      readonly overwritten: readonly string[];
      /** The stacks after the step (the entry moved to the other stack as its own inverse). */
      readonly stacks: UndoStacks;
    }
  | { readonly ok: false; readonly reason: 'empty' | 'overwritten' | 'failed'; readonly message: string; readonly stacks: UndoStacks };

/**
 * Plan one undo (`direction: 'undo'`) or redo: the top entry of that stack,
 * inverted against the rows as they are now (`current`), minus the fields
 * others overwrote. `meta` stamps each inverse (a fresh HLC and clientOpId).
 * An entry with nothing left to undo is dropped and reported.
 */
export function planStep(stacks: UndoStacks, direction: 'undo' | 'redo', current: (rowId: string) => Row | null, meta: () => InverseMeta, nowMs: number): UndoPlan {
  const from = direction === 'undo' ? stacks.undo : stacks.redo;
  const entry = from.at(-1);
  if (entry === undefined) return { ok: false, reason: 'empty', message: `nothing to ${direction}`, stacks };
  const rest = from.slice(0, -1);
  const without: UndoStacks = direction === 'undo' ? { undo: rest, redo: stacks.redo, restored: stacks.restored } : { undo: stacks.undo, redo: rest, restored: stacks.restored };
  const restored = new Map(stacks.restored);
  const patches: RowPatch[] = [];
  const items: UndoItem[] = [];
  const overwritten: string[] = [];
  for (const item of entry.items) {
    const rowNow = current(item.patch.rowId);
    const ours = undoableFields(item.patch, rowNow, supersededBy(stacks.restored, item.patch.rowId));
    overwritten.push(...ours.overwritten);
    if (Object.keys(ours.patch.set).length === 0 && Object.keys(ours.patch.text ?? {}).length === 0) continue;
    const inverse = inversePatch(item.rowBefore, ours.patch, meta());
    if (!inverse.ok) return { ok: false, reason: 'failed', message: inverse.error, stacks: without };
    patches.push(inverse.patch);
    items.push({ rowBefore: rowNow, patch: inverse.patch });
    recordRestored(restored, item.rowBefore, ours.patch, inverse.patch.hlc);
  }
  if (patches.length === 0) {
    return { ok: false, reason: 'overwritten', message: `nothing left to ${direction}: someone else changed ${overwritten.join(', ')} since`, stacks: without };
  }
  const moved: UndoEntry = { items, atMs: nowMs };
  const stacksAfter: UndoStacks = direction === 'undo' ? { undo: rest, redo: [...stacks.redo, moved], restored } : { undo: [...stacks.undo, moved], redo: rest, restored };
  return { ok: true, patches, overwritten, stacks: stacksAfter };
}
